<template>
  <VeeForm v-slot="{ handleSubmit }" :validation-schema="validationSchema" :initial-values="initialFormValues" as="div">
    <form @submit="handleSubmit($event, onContinue)">
      <div class="bg-white p-4 rounded-md">
        <p class="font-bold text-xl pb-2 tracking-tight text-gray-900">Conseil et formule</p>
        <p class="text-xs italic text-gray-600 pb-2">
          Vos réponses nous permettent de vous proposer la solution d'assurance la plus adaptée à vos besoins.
          <br />
          Merci de répondre de façon complète et sincère.
        </p>

        <!-- Champs cachés pour la validation VeeValidate -->
        <Field name="wantsInsurance" v-model="local.wantsInsurance" type="text" class="hidden" />
        <Field name="usageType" v-model="local.usageType" type="text" class="hidden" />
        <Field name="offerType" v-model="local.offerType" type="text" class="hidden" />
        <Field name="risks" v-model="local.risks" as="div" class="hidden" />
        <Field name="hasExistingInsurance" v-model="local.hasExistingInsurance" type="text" class="hidden" />
        <Field name="existingInsuranceReason" v-model="local.existingInsuranceReason" type="text" class="hidden" />
        <Field
          name="existingInsuranceReasonOtherText"
          v-model="local.existingInsuranceReasonOtherText"
          type="text"
          class="hidden"
        />
        <Field name="electronicDevicesValue" v-model="local.electronicDevicesValue" type="text" class="hidden" />

        <div class="mt-2 space-y-3">
          <!-- 1. PROFIL DE L'ASSURÉ -->
          <AdvisoryRadioGroup
            v-model="local.usageType"
            name="usageType"
            label="Profil de l'assuré"
            description="Afin de vous proposer une assurance adaptée, merci de préciser le profil de l'assuré."
            :options="usageTypeOptions"
            translation-prefix="usageType"
          />

          <!-- 2. Souhaitez-vous souscrire à une assurance ? -->
          <AdvisoryRadioGroup
            v-model="local.wantsInsurance"
            name="wantsInsurance"
            label="Souhaitez-vous souscrire à une assurance ?"
            :options="wantsInsuranceOptions"
            translation-prefix="wantsInsurance"
          />

          <template v-if="local.wantsInsurance === true">
            <!-- 3. TYPE D'OFFRE -->
            <OfferTypeSection v-if="!hasSingleOfferType" v-model="local.offerType" />

            <!-- 3.5. VALEUR DES APPAREILS ÉLECTRONIQUES (si Connect) -->
            <AdvisoryRadioGroup
              v-if="local.offerType === 'connect_protection'"
              v-model="local.electronicDevicesValue"
              name="electronicDevicesValue"
              label="Valeur des appareils électroniques"
              description="Quelle est la valeur des appareils électroniques pouvant se connecter à internet ?"
              :options="electronicDevicesValueOptions"
              translation-prefix="electronicDevicesValue"
            />

            <!-- 4. RISQUES À COUVRIR -->
            <RiskSelectionSection v-model="local.risks" :offer-type="local.offerType" :options="riskOptions" />

            <!-- 5. EXISTENCE CUMUL D'ASSURANCE -->
            <ExistingInsuranceSection
              :has-existing-insurance="local.hasExistingInsurance"
              :existing-insurance-reason="local.existingInsuranceReason"
              :existing-insurance-reason-other-text="local.existingInsuranceReasonOtherText"
              @update:has-existing-insurance="local.hasExistingInsurance = $event"
              @update:existing-insurance-reason="local.existingInsuranceReason = $event"
              @update:existing-insurance-reason-other-text="local.existingInsuranceReasonOtherText = $event"
              @show-cumul-modal="showCumulModal = true"
            />

            <div class="mt-4 flex justify-end">
              <SecondaryButton
                type="submit"
                :disabled="isContinueBlocked"
                :class="{ 'cursor-not-allowed opacity-60': isContinueBlocked }"
              >
                Continuer
              </SecondaryButton>
            </div>
          </template>
        </div>
      </div>

      <!-- Modal Cumul d'assurance -->
      <ExistingInsuranceModal :open="showCumulModal" @close="showCumulModal = false" />
    </form>
  </VeeForm>
</template>

<script>
import { Form as VeeForm, Field } from "vee-validate";
import { SecondaryButton } from "@estaly/ui";
import ExistingInsuranceModal from "@/components/pos/subscription/formwithsteps/ExistingInsuranceModal.vue";
import AdvisoryRadioGroup from "@/components/pos/subscription/formwithsteps/AdvisoryRadioGroup.vue";
import ExistingInsuranceSection from "@/components/pos/subscription/formwithsteps/ExistingInsuranceSection.vue";
import OfferTypeSection from "@/components/pos/subscription/formwithsteps/OfferTypeSection.vue";
import RiskSelectionSection from "@/components/pos/subscription/formwithsteps/RiskSelectionSection.vue";
import {
  advisoryFormSchema,
  defaultAdvisory,
  RISK_OPTIONS,
  USAGE_TYPE_OPTIONS,
  ELECTRONIC_DEVICES_VALUE_OPTIONS,
  WANTS_INSURANCE_OPTIONS,
} from "@/components/pos/subscription/formwithsteps/advisoryFormConfig";

export default {
  components: {
    VeeForm,
    Field,
    ExistingInsuranceModal,
    AdvisoryRadioGroup,
    ExistingInsuranceSection,
    OfferTypeSection,
    RiskSelectionSection,
    SecondaryButton,
  },
  props: {
    modelValue: {
      type: Object,
      default: () => defaultAdvisory(),
    },
    availableOfferTypes: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["update:modelValue", "continue", "cancel"],
  data() {
    return {
      validationSchema: advisoryFormSchema,
      riskOptions: RISK_OPTIONS,
      usageTypeOptions: USAGE_TYPE_OPTIONS,
      wantsInsuranceOptions: WANTS_INSURANCE_OPTIONS,
      electronicDevicesValueOptions: ELECTRONIC_DEVICES_VALUE_OPTIONS,
      local: { ...defaultAdvisory(), ...this.modelValue },
      syncingFromModel: false,
      showCumulModal: false,
    };
  },
  computed: {
    isContinueBlocked() {
      return (
        this.local.wantsInsurance !== true ||
        (this.local.risks.includes("breakdown") && this.local.offerType === "mobile_protection")
      );
    },
    initialFormValues() {
      return { ...defaultAdvisory(), ...this.modelValue };
    },
    hasSingleOfferType() {
      return this.availableOfferTypes.length === 1;
    },
  },
  watch: {
    modelValue: {
      handler(val) {
        if (val && typeof val === "object" && Object.keys(val).length > 0) {
          this.syncingFromModel = true;
          this.local = { ...defaultAdvisory(), ...val };
          this.$nextTick(() => {
            this.syncingFromModel = false;
          });
        }
      },
      deep: true,
    },
    "local.offerType": {
      handler(newVal, oldVal) {
        if (oldVal === "connect_protection" && newVal !== "connect_protection") {
          this.local = { ...this.local, electronicDevicesValue: null };
        }
      },
    },
    "local.hasExistingInsurance": {
      handler(newVal) {
        if (newVal === false) {
          this.local = { ...this.local, existingInsuranceReason: null, existingInsuranceReasonOtherText: "" };
        }
      },
    },
    "local.wantsInsurance": {
      handler(newVal, oldVal) {
        if (newVal === false && oldVal !== false) {
          this.local = { ...this.local, recommendedOffer: null, recommendedOfferId: null };
          this.$emit("cancel");
        }
        if (newVal === true && this.hasSingleOfferType) {
          this.local = { ...this.local, offerType: this.availableOfferTypes[0] };
        }
      },
    },
    local: {
      deep: true,
      handler() {
        if (!this.syncingFromModel) {
          this.$emit("update:modelValue", { ...this.local });
        }
      },
    },
  },
  methods: {
    onContinue() {
      this.$emit("continue");
    },
  },
};
</script>
